import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Camera, X, ArrowUpRight } from "lucide-react";

export const REGISTER_URL = "https://register.loveexclusivephotography.co.uk/";
const SESSION_KEY = "lep_photoday_seen";
const HERO_IMG = "/brand/photoday-hero.webp";
const EASE = [0.16, 1, 0.3, 1];

function useFabLayout() {
  const read = () => {
    const el = document.querySelector('[data-testid="cookies-notice"]');
    return {
      offset: el && window.innerWidth < 640 ? el.offsetHeight + 12 : 0,
      hidden: !!document.querySelector('[role="dialog"][data-state="open"]'),
    };
  };
  const [layout, setLayout] = useState(read);
  useEffect(() => {
    const measure = () => setLayout(read());
    const id = setInterval(measure, 300);
    window.addEventListener("resize", measure);
    return () => { clearInterval(id); window.removeEventListener("resize", measure); };
  }, []);
  return layout;
}

function FloatingButton({ onOpen }) {
  const reduce = useReducedMotion();
  const { offset, hidden } = useFabLayout();
  if (hidden) return null;
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      data-testid="photoday-floating-btn"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.45, ease: EASE }}
      className="fixed z-[65] left-4 sm:left-6"
      style={{ bottom: `calc(max(16px, env(safe-area-inset-bottom)) + ${offset}px)` }}
    >
      <motion.span
        animate={reduce ? undefined : { y: [0, -3, 0] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
        whileHover={{ scale: 1.04 }}
        className="flex items-center gap-2.5 rounded-full bg-black text-white pl-3.5 pr-5 py-3 shadow-[0_14px_34px_-10px_rgba(0,0,0,0.55)] font-body text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-bold"
        style={{ willChange: "transform" }}
      >
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/15">
          <Camera size={13} strokeWidth={2} />
        </span>
        Register for Photo Day
      </motion.span>
    </motion.button>
  );
}

function PopupCard({ onClose }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={{ opacity: 0, y: 36 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 24 }}
      transition={{ duration: 0.7, ease: EASE }}
      className="relative w-full max-w-[580px]"
      onClick={(e) => e.stopPropagation()}
    >
      <motion.div
        animate={reduce ? undefined : { y: [0, -6, 0] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="photoday-title"
        data-testid="photoday-popup"
        className="relative rounded-[24px] bg-[#FDFBF7] overflow-hidden shadow-[0_40px_90px_-24px_rgba(0,0,0,0.45),0_8px_24px_-12px_rgba(0,0,0,0.2)]"
        style={{ willChange: "transform" }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="photoday-close-btn"
          className="absolute top-3.5 right-3.5 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur-md text-black flex items-center justify-center shadow-md hover:bg-white hover:rotate-90 transition-transform duration-300"
        >
          <X size={16} strokeWidth={2.2} />
        </button>
        <div className="max-h-[calc(100dvh-48px)] overflow-y-auto overscroll-contain">
          <div className="relative aspect-[3/2] sm:aspect-[16/10] bg-[#BFDDF5]">
            <img
              src={HERO_IMG}
              alt="Happy children smiling down into the camera"
              data-testid="photoday-hero-img"
              className="absolute inset-0 w-full h-full object-cover object-center"
              decoding="async"
            />
          </div>
          <div className="px-6 sm:px-10 pt-6 sm:pt-7 pb-7 sm:pb-9 text-center">
            <h2
              id="photoday-title"
              data-testid="photoday-heading"
              className="font-display font-bold uppercase text-[26px] sm:text-[34px] tracking-[0.01em] leading-[1.05] sm:whitespace-nowrap text-black"
            >
              Registering for Photo Day?
            </h2>
            <p data-testid="photoday-main-message" className="mt-4 font-body text-[14px] sm:text-[15px] leading-relaxed text-black/75">
              If your child’s nursery or school has asked you to register for their upcoming photography day, you're in the right place!
            </p>
            <p data-testid="photoday-pin-message" className="mt-4 mx-auto inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 font-body text-[12px] sm:text-[13px] font-semibold text-black">
              You'll need the 4-digit PIN provided by your nursery or school.
            </p>
            <div className="mt-6">
              <motion.a
                href={REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="photoday-cta-btn"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-black text-white px-5 min-[360px]:px-7 py-4 font-body text-[10px] min-[360px]:text-[11px] sm:text-[12px] uppercase tracking-[0.14em] min-[360px]:tracking-[0.25em] font-bold shadow-[0_16px_30px_-14px_rgba(0,0,0,0.6)]"
              >
                Register for Photo Day
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </motion.a>
            </div>
            <p data-testid="photoday-already-registered" className="mt-4 font-body text-[12px] sm:text-[13px] text-black/60">
              Already registered? You don't need to register again.
            </p>
            <div className="mt-6 pt-5 border-t border-black/10">
              <p data-testid="photoday-footer" className="font-body text-[11px] leading-relaxed text-black/45">
                For general enquiries, please use our Contact Us page. Photo Day registration must be completed through the registration portal above.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function RegistrationPopup() {
  const [open, setOpen] = useState(false);
  const [minimised, setMinimised] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) {
      setMinimised(true);
      return;
    }
    const timer = setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY, "1");
      setOpen(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => setOpen(false);
  const reopen = () => { setMinimised(false); setOpen(true); };

  return (
    <>
      <AnimatePresence onExitComplete={() => setMinimised(true)}>
        {open && (
          <motion.div
            key="overlay"
            data-testid="photoday-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            onClick={close}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 backdrop-blur-[6px] px-4"
            style={{ paddingTop: "max(24px, env(safe-area-inset-top))", paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
          >
            <PopupCard onClose={close} />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {minimised && !open && <FloatingButton key="fab" onOpen={reopen} />}
      </AnimatePresence>
    </>
  );
}
